import { useState, useEffect, FC } from 'react';
import { getRequest, postRequest } from '../utils/api';
import { Incident } from '../interfaces/incident';
import { Alert } from '../interfaces/alert';
import { formatDate } from '../utils/datetime';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Acknowledgment } from '../interfaces/acknowledgment';

const Incidents: FC = () => {
  const { user } = useAuth(); // Get logged-in user details
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [openIncidents, setOpenIncidents] = useState<Incident[]>([]);
  const [resolvedIncidents, setResolvedIncidents] = useState<Incident[]>([]);
  const [acknowledgments, setAcknowledgments] = useState<Acknowledgment[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize] = useState<number>(25);

  useEffect(() => {
    fetchOpenIncidents();
    fetchResolvedIncidents();
    if (user) {
      fetchAcknowledgments(user.id);
    }
  }, [currentPage, user]);

  const fetchOpenIncidents = async () => {
    try {
      const data = await getRequest<{ incidents: Incident[] }>(
        '/incidents?status=open,in_progress',
      );
      if (data) {
        setOpenIncidents(data.incidents);
      }
    } catch (error) {
      console.error('Error fetching open incidents:', error);
    }
  };

  const fetchResolvedIncidents = async () => {
    try {
      const data = await getRequest<{
        incidents: Incident[];
        totalPages: number;
      }>(`/incidents?status=resolved&page=${currentPage}&limit=${pageSize}`);
      if (data) {
        setResolvedIncidents(data.incidents);
        setTotalPages(data.totalPages);
      }
    } catch (error) {
      console.error('Error fetching resolved incidents:', error);
    }
  };

  const fetchAcknowledgments = async (userId: number) => {
    try {
      const data = await getRequest<Acknowledgment[]>(
        `/acknowledgments?user_id=${userId}`,
      );
      if (data) {
        setAcknowledgments(data);
      }
    } catch (error) {
      console.error('Error fetching acknowledged incidents:', error);
    }
  };

  const acknowledgeIncident = async (id: number) => {
    try {
      await postRequest(`/incidents/${id}/acknowledge`);
      // Optimistically update state
      const acknowledgement = {
        user: { id: user?.id, name: user?.name },
        incident: { id },
        acknowledged_at: new Date().toISOString(),
      } as Acknowledgment;
      setAcknowledgments((prev) => [...prev, acknowledgement]);
    } catch (error) {
      console.error('Error acknowledging incident:', error);
    }
  };

  const resolveIncident = async (id: number) => {
    try {
      await postRequest(`/incidents/${id}/resolve`);
      fetchOpenIncidents();
      fetchResolvedIncidents();
    } catch (error) {
      console.error('Error resolving incident:', error);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div>
      <h2 className="mb-3">Open Incidents</h2>
      <table className="table table-striped table-bordered">
        <thead className="table-dark">
          <tr>
            <th>ID</th>
            <th>Created At</th>
            <th>Priority</th>
            <th>System</th>
            <th>Description</th>
            <th>Status</th>
            <th>Acknowledged At</th>
            <th>Acknowledged</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {openIncidents.map((incident) => {
            // Find acknowledgment for this incident (if any)
            const acknowledgment = acknowledgments.find(
              (ack) => ack.incident.id === incident.id,
            );
            const hasAcknowledged = !!acknowledgment;

            return (
              <tr key={incident.id}>
                <td>
                  <Link to={`/incidents/${incident.id}`}>{incident.id}</Link>
                </td>
                <td>{formatDate(incident.created_at)}</td>
                <td>{incident.priority}</td>
                <td>{incident.system?.name}</td>
                <td>{incident.description}</td>
                <td>{incident.status}</td>
                <td>
                  {' '}
                  {acknowledgment
                    ? formatDate(acknowledgment.acknowledged_at)
                    : '—'}
                </td>
                <td>{hasAcknowledged ? 'Yes' : 'No'}</td>
                <td>
                  {!hasAcknowledged && (
                    <button
                      className="btn btn-primary"
                      onClick={() => acknowledgeIncident(incident.id)}
                    >
                      Acknowledge
                    </button>
                  )}
                  {hasAcknowledged && (
                    <button
                      className="btn btn-primary"
                      onClick={() => resolveIncident(incident.id)}
                    >
                      Resolve
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <h2 className="mb-3">Past Incidents</h2>
      <table className="table table-striped table-bordered">
        <thead className="table-dark">
          <tr>
            <th>ID</th>
            <th>Created At</th>
            <th>Priority</th>
            <th>System</th>
            <th>Description</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {resolvedIncidents.map((incident) => (
            <tr key={incident.id}>
              <td>
                <Link to={`/incidents/${incident.id}`}>{incident.id}</Link>
              </td>
              <td>{formatDate(incident.created_at)}</td>
              <td>{incident.priority}</td>
              <td>{incident.system?.name}</td>
              <td>{incident.description}</td>
              <td>{incident.status}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pagination">
        <button
          className="btn btn-secondary"
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          Previous
        </button>
        <span className="mx-2">
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="btn btn-secondary"
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Incidents;
